export const CAPTURE_DISABLED_REASON = "חיוב J5 עדיין לא פעיל — חסר אימות PayMe";

const statusClasses = {
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  active: "border-amber-200 bg-amber-50 text-amber-800",
  waiting: "border-orange-200 bg-orange-50 text-orange-800",
  released: "border-sky-200 bg-sky-50 text-sky-800",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  pending: "border-indigo-200 bg-indigo-50 text-indigo-800",
  danger: "border-rose-200 bg-rose-50 text-rose-800",
};

const approvedOpenCopy = {
  admin: {
    label: "בהמתנה לחיוב מנהל",
    description: "העסקה אושרה וממתינה לחיוב מנהל",
  },
  supplier: {
    label: "בהמתנה לחיוב מנהל",
    description: "העסקה אושרה וממתינה לחיוב מנהל",
  },
  dealer: {
    label: "שריון פעיל",
    description: "העסקה אושרה וממתינה לחיוב מנהל",
  },
};

function truthy(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function auctionPhase(row = {}) {
  if (!truthy(row.is_auction ?? row.isAuction)) return "";
  if (truthy(row.auction_is_active)) return "active";
  if (truthy(row.auction_has_ended)) return "ended";

  const status = String(row.auction_status || row.status || "").toLowerCase();
  if (["ended", "finalized"].includes(status)) return "ended";
  if (["active", "live"].includes(status)) return "active";

  const start = row.auction_start_datetime || row.auction_start_at;
  if (start) {
    const startTime = new Date(String(start).replace(" ", "T")).getTime();
    if (!Number.isNaN(startTime) && startTime > Date.now()) return "future";
  }

  return "inactive";
}

function auctionHoldCopy(row = {}) {
  const phase = auctionPhase(row);
  if (!phase) return null;

  if (phase === "active") {
    return {
      statusKey: "auction_active",
      label: "מכרז פעיל",
      description: "קיימת תפיסת מסגרת פעילה עבור מכרז פעיל",
    };
  }

  if (phase === "future") {
    return {
      statusKey: "auction_future",
      label: "מכרז עתידי",
      description: "קיימת תפיסת מסגרת עבור מכרז שטרם התחיל",
    };
  }

  if (phase === "ended") {
    return {
      statusKey: "auction_ended",
      label: "מכרז הסתיים",
      description: "קיימת תפיסת מסגרת עבור מכרז שכבר הסתיים",
    };
  }

  return {
    statusKey: "auction_inactive",
    label: "מכרז לא פעיל",
    description: "קיימת תפיסת מסגרת עבור מכרז שאינו פעיל כרגע",
  };
}

export function hasJ5(row = {}) {
  return Boolean(
    row.reservation_hold_id_actual ||
      row.reservation_hold_id ||
      row.hold_id ||
      row.payme_sale_id ||
      row.reservation_hold_status
  );
}

export function hasActiveJ5Hold(row = {}) {
  const holdStatus = String(row.reservation_hold_status || row.hold_status || "").toLowerCase();
  return Boolean(row.reservation_hold_id_actual || row.reservation_hold_id || row.hold_id) &&
    ["active", "open"].includes(holdStatus);
}

function makeStatus({
  statusKey,
  label,
  description,
  variant = "neutral",
  isJ5 = true,
  isActionableByAdmin = false,
}) {
  return {
    statusKey,
    label,
    description,
    variant,
    className: statusClasses[variant] || statusClasses.neutral,
    isActionableByAdmin,
    canCapture: false,
    disabledReason: isActionableByAdmin ? CAPTURE_DISABLED_REASON : "",
    isJ5,
  };
}

export function getJ5Status(row = {}, role = "dealer") {
  const holdStatus = String(row.reservation_hold_status || row.hold_status || "").toLowerCase();
  const transactionStatus = String(row.last_billing_transaction_status || "").toLowerCase();
  const transactionType = String(row.last_billing_transaction_type || "");
  const releaseReason = String(row.release_reason || "");
  const approved = Number(row.offer_status) === 2 || Boolean(row.approved_at);
  const immediatePurchase = truthy(row.immediate_purchase);
  const immediatePurchaseSucceeded =
    truthy(row.immediate_purchase_succeeded) ||
    (immediatePurchase && transactionStatus === "success" && transactionType === "immediate_purchase");
  const immediatePurchasePending =
    truthy(row.immediate_purchase_pending) ||
    (immediatePurchase && transactionStatus === "pending" && transactionType === "immediate_purchase");
  const captureSucceeded =
    truthy(row.deal_charge_succeeded) ||
    truthy(row.capture_succeeded) ||
    ["captured", "charged"].includes(holdStatus);
  const capturePending =
    truthy(row.deal_charge_pending) ||
    truthy(row.capture_pending) ||
    (transactionStatus === "pending" &&
      ["deal_charge", "full_charge", "capture"].includes(transactionType));

  if (immediatePurchaseSucceeded) {
    return makeStatus({
      statusKey: "immediate_purchase_charged",
      label: "רכישה מידית חויבה",
      description: "בוצעה רכישה מידית והחיוב הושלם בהצלחה",
      variant: "success",
      isJ5: false,
    });
  }

  if (immediatePurchasePending) {
    return makeStatus({
      statusKey: "immediate_purchase_pending",
      label: "רכישה מידית בתהליך",
      description: "חיוב רכישה מידית נרשם וממתין להשלמה",
      variant: "pending",
      isJ5: false,
    });
  }

  if (!hasJ5(row)) {
    return makeStatus({
      statusKey: "no_j5",
      label: "עסקה ללא שריון",
      description: "לעסקה זו לא בוצעה תפיסת מסגרת",
      variant: "neutral",
      isJ5: false,
    });
  }

  if (captureSucceeded) {
    const capturedCopy = {
      admin: {
        label: "העסקה חויבה",
        description: "העסקה חויבה",
      },
      dealer: {
        label: "העסקה חויבה",
        description: "העסקה חויבה",
      },
      supplier: {
        label: "העסקה חויבה",
        description: "העסקה חויבה",
      },
    };
    const copy = capturedCopy[role] || capturedCopy.dealer;

    return makeStatus({
      statusKey: "captured",
      label: "העסקה חויבה",
      description: "העסקה חויבה",
      ...copy,
      variant: "success",
    });
  }

  if (capturePending) {
    return makeStatus({
      statusKey: "capture_pending",
      label: "חיוב בתהליך",
      description: "פעולת חיוב נרשמה וממתינה להשלמה",
      variant: "pending",
    });
  }

  if (
    ["failed", "release_failed", "capture_failed"].includes(holdStatus) ||
    transactionStatus === "failed"
  ) {
    return makeStatus({
      statusKey: "failed",
      label: "פעולת חיוב נכשלה",
      description: row.last_billing_transaction_failure_reason || row.last_error || "פעולת J5 לא הושלמה בהצלחה",
      variant: "danger",
    });
  }

  if (holdStatus === "released") {
    const releasedAfter48h = releaseReason === "released_after_48h_from_supplier_approval";

    return makeStatus({
      statusKey: releasedAfter48h ? "released_after_48h_from_supplier_approval" : "released",
      label: releasedAfter48h ? "השריון שוחרר לאחר 48 שעות" : "השריון שוחרר",
      description: releasedAfter48h
        ? "השריון שוחרר לאחר 48 שעות מאישור העסקה"
        : "תפיסת המסגרת שוחררה",
      variant: "released",
    });
  }

  if (holdStatus === "active" && approved) {
    const auctionCopy = auctionHoldCopy(row);
    if (auctionCopy) {
      return makeStatus({
        ...auctionCopy,
        variant: auctionCopy.statusKey === "auction_active" ? "active" : "neutral",
      });
    }

    const copy = approvedOpenCopy[role] || approvedOpenCopy.dealer;

    return makeStatus({
      statusKey: "pending_capture",
      label: copy.label,
      description: copy.description,
      variant: role === "admin" ? "waiting" : "active",
      isActionableByAdmin: role === "admin",
    });
  }

  if (holdStatus === "active" || holdStatus === "open") {
    const auctionCopy = auctionHoldCopy(row);
    if (auctionCopy) {
      return makeStatus({
        ...auctionCopy,
        variant: auctionCopy.statusKey === "auction_active" ? "active" : "neutral",
      });
    }

    return makeStatus({
      statusKey: "j5_active",
      label: "שריון פעיל",
      description: "קיימת תפיסת מסגרת פעילה",
      variant: "active",
    });
  }

  return makeStatus({
    statusKey: holdStatus || "j5_unknown",
    label: holdStatus || "סטטוס לא ידוע",
    description: "סטטוס השריון כפי שחזר מהמערכת",
    variant: "neutral",
  });
}

export function getDealType(row = {}) {
  if (truthy(row.immediate_purchase)) return "רכישה מידית";
  return hasJ5(row) ? "J5" : "ללא J5";
}

export function isOpenJ5Status(statusKey) {
  return [
    "pending_capture",
    "j5_active",
    "auction_active",
    "auction_future",
    "auction_ended",
    "auction_inactive",
  ].includes(statusKey);
}

export function isReleasedJ5Status(statusKey) {
  return ["released", "released_after_48h_from_supplier_approval"].includes(statusKey);
}
